import { useCallback, useEffect, useState } from "react";
import { Save, RefreshCw, Users, Megaphone, Info } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";

/**
 * Admin · Commission Settings (unified)
 *
 * Refer (user-to-user) and Affiliate (branded promo) are TWO SEPARATE channels.
 * This page shows both side-by-side so an admin can configure them at once
 * — typically: refer = lower %, affiliate = higher % + tiered scheme.
 *
 * Backend endpoints (already exist, untouched):
 *   GET/PUT  /admin/referrals/loss-commission/config   → refer (loss-based)
 *   GET/PUT  /admin/affiliate-settings                 → affiliate (period revshare)
 */
export default function CommissionSettings() {
  const [refer, setRefer] = useState(null);
  const [aff, setAff] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingRefer, setSavingRefer] = useState(false);
  const [savingAff, setSavingAff] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, a] = await Promise.all([
        api.get("/admin/referrals/loss-commission/config"),
        api.get("/affiliate-settings"),
      ]);
      setRefer({
        enabled: !!r.data.enabled,
        pct: Number(r.data.pct ?? 0),
        min_loss_threshold: Number(r.data.min_loss_threshold ?? 0),
        min_payout: Number(r.data.min_payout ?? 0),
      });
      setAff(a.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load commission settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveRefer = async () => {
    if (refer.pct < 0 || refer.pct > 100) {
      toast.error("Refer commission % must be between 0 and 100");
      return;
    }
    setSavingRefer(true);
    try {
      const { data } = await api.put("/admin/referrals/loss-commission/config", refer);
      setRefer({
        enabled: !!data.enabled,
        pct: Number(data.pct ?? 0),
        min_loss_threshold: Number(data.min_loss_threshold ?? 0),
        min_payout: Number(data.min_payout ?? 0),
      });
      toast.success("Refer commission saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingRefer(false);
    }
  };

  const saveAff = async () => {
    if ((aff.affiliate_commission_pct ?? 0) < 0 || (aff.affiliate_commission_pct ?? 0) > 100) {
      toast.error("Affiliate commission % must be between 0 and 100");
      return;
    }
    setSavingAff(true);
    try {
      await api.put("/affiliate-settings", aff);
      toast.success("Affiliate commission saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingAff(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="commission-settings-page">
      <PageHeader
        title="Commission Settings"
        subtitle="Refer (user-to-user) and Affiliate (branded promo) এর জন্য আলাদা commission rate এখানে configure করুন।"
        actions={
          <button
            onClick={load}
            className="h-9 px-3 border border-gray-300 rounded-sm text-sm flex items-center gap-1.5 hover:bg-gray-50"
            data-testid="cs-reload"
          >
            <RefreshCw size={14} /> Reload
          </button>
        }
      />

      {/* Channel comparison banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 flex items-start gap-2 text-[12px] text-blue-900" data-testid="cs-info-banner">
        <Info size={16} className="mt-0.5 shrink-0" />
        <div className="leading-relaxed">
          <b>Refer</b> = ব্যক্তিগত user-to-user signup link। Commission referee এর <b>net loss</b> থেকে calculate হয়।{" "}
          <b>Affiliate</b> = branded promo code, পুরো payout-period জুড়ে net loss থেকে commission হিসাব হয় এবং tier system support করে।
          দুটো channel স্বাধীনভাবে count হয় — refer signup শুধু refer dashboard এ, affiliate signup শুধু affiliate dashboard এ।
        </div>
      </div>

      {loading || !refer || !aff ? (
        <div className="p-10 text-center text-sm text-gray-500" data-testid="cs-loading">Loading commission settings…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* ============ Refer ============ */}
          <Card
            tone="emerald"
            icon={Users}
            title="Refer Commission"
            subtitle="User-to-user invite, paid per net-loss event"
            testId="cs-refer-card"
          >
            <ToggleRow
              label="Enable refer commission"
              hint="Off হলে admin Pay বাটনে ক্লিক করলেও কিছু credit হবে না।"
              checked={refer.enabled}
              onChange={(v) => setRefer({ ...refer, enabled: v })}
              testId="cs-refer-enabled"
              tone="emerald"
            />
            <PctField
              label="Commission % of referee's net loss"
              value={refer.pct}
              onChange={(v) => setRefer({ ...refer, pct: v })}
              testId="cs-refer-pct"
              hint="Example: 10% — referee ৳1,000 net loss করলে inviter এর wallet এ ৳100 যোগ হবে।"
              tone="emerald"
            />
            <div className="grid grid-cols-2 gap-3">
              <NumField
                label="Min loss threshold (৳)"
                value={refer.min_loss_threshold}
                onChange={(v) => setRefer({ ...refer, min_loss_threshold: v })}
                step="0.01"
                testId="cs-refer-min-loss"
                hint="এই amount এর নিচে loss হলে commission accrue হবে না।"
              />
              <NumField
                label="Min payout (৳)"
                value={refer.min_payout}
                onChange={(v) => setRefer({ ...refer, min_payout: v })}
                step="0.01"
                testId="cs-refer-min-payout"
                hint="এই amount এর নিচে Pay বাটন block থাকবে।"
              />
            </div>
            <SaveBar
              onSave={saveRefer}
              busy={savingRefer}
              tone="emerald"
              testId="cs-refer-save"
            />
          </Card>

          {/* ============ Affiliate ============ */}
          <Card
            tone="purple"
            icon={Megaphone}
            title="Affiliate Commission"
            subtitle="Branded promo code, period-based revshare"
            testId="cs-affiliate-card"
          >
            <ToggleRow
              label="Enable affiliate program"
              hint="Off হলে affiliate dashboard এ commission generate হবে না।"
              checked={!!aff.affiliate_enabled}
              onChange={(v) => setAff({ ...aff, affiliate_enabled: v })}
              testId="cs-aff-enabled"
              tone="purple"
            />
            <PctField
              label="Default commission %"
              value={aff.affiliate_commission_pct ?? 0}
              onChange={(v) => setAff({ ...aff, affiliate_commission_pct: v })}
              testId="cs-aff-pct"
              hint="Tier system off থাকলে এই % সব affiliate এর জন্য apply হবে।"
              tone="purple"
            />
            <div className="grid grid-cols-2 gap-3">
              <NumField
                label="Payout period (days)"
                value={aff.affiliate_period_days ?? 7}
                onChange={(v) => setAff({ ...aff, affiliate_period_days: Math.max(1, Math.round(v)) })}
                step="1"
                min="1"
                testId="cs-aff-period"
                hint="Net loss এই period এর মধ্যে aggregate হবে।"
              />
              <NumField
                label="Min payout (৳)"
                value={aff.affiliate_min_payout ?? 0}
                onChange={(v) => setAff({ ...aff, affiliate_min_payout: v })}
                step="1"
                testId="cs-aff-min-payout"
                hint="এই amount এর নিচে commission carry-forward হবে।"
              />
            </div>
            <ToggleRow
              label="Tiered commission (RevShare)"
              hint="Net-loss এর উপর ভিত্তি করে % নির্ধারণ হবে (Affiliate Program পেইজে edit করুন)।"
              checked={!!aff.affiliate_tiers_enabled}
              onChange={(v) => setAff({ ...aff, affiliate_tiers_enabled: v })}
              testId="cs-aff-tiers-enabled"
              tone="purple"
            />
            <SaveBar
              onSave={saveAff}
              busy={savingAff}
              tone="purple"
              testId="cs-aff-save"
            />
          </Card>
        </div>
      )}
    </div>
  );
}

/* ----------------------------- Atoms ----------------------------- */

const TONE = {
  emerald: { bar: "bg-emerald-500 hover:bg-emerald-600", chip: "bg-emerald-50 text-emerald-700 border-emerald-200", accent: "accent-emerald-600", icon: "text-emerald-600" },
  purple: { bar: "bg-purple-500 hover:bg-purple-600", chip: "bg-purple-50 text-purple-700 border-purple-200", accent: "accent-purple-600", icon: "text-purple-600" },
};

function Card({ tone = "emerald", icon: Icon, title, subtitle, children, testId }) {
  const t = TONE[tone];
  return (
    <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid={testId}>
      <div className="px-4 py-3 border-b border-gray-100 flex items-start gap-3">
        <div className={`h-9 w-9 rounded-sm border ${t.chip} flex items-center justify-center shrink-0`}>
          {Icon && <Icon size={16} className={t.icon} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-gray-900">{title}</div>
          <div className="text-[11px] text-gray-500 leading-tight">{subtitle}</div>
        </div>
      </div>
      <div className="p-4 space-y-4">{children}</div>
    </div>
  );
}

function ToggleRow({ label, hint, checked, onChange, testId, tone = "emerald" }) {
  const t = TONE[tone];
  return (
    <label className="flex items-center justify-between gap-3 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50">
      <div className="min-w-0">
        <div className="text-sm font-semibold text-gray-900">{label}</div>
        {hint && <div className="text-[11px] text-gray-500 leading-tight">{hint}</div>}
      </div>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className={`h-5 w-5 ${t.accent}`}
        data-testid={testId}
      />
    </label>
  );
}

function PctField({ label, value, onChange, testId, hint, tone = "emerald" }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">{label}</label>
      <div className="relative">
        <input
          type="number"
          step="0.1"
          min="0"
          max="100"
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          className="w-full h-10 pl-3 pr-8 border border-gray-200 rounded-sm text-sm tabular focus:border-gray-400 outline-none"
          data-testid={testId}
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
      </div>
      {hint && <div className="text-[11px] text-gray-500 mt-1">{hint}</div>}
    </div>
  );
}

function NumField({ label, value, onChange, step = "1", min = "0", testId, hint }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">{label}</label>
      <input
        type="number"
        step={step}
        min={min}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular focus:border-gray-400 outline-none"
        data-testid={testId}
      />
      {hint && <div className="text-[11px] text-gray-500 mt-1">{hint}</div>}
    </div>
  );
}

function SaveBar({ onSave, busy, tone = "emerald", testId }) {
  const t = TONE[tone];
  return (
    <div className="flex justify-end pt-2 border-t border-gray-100">
      <button
        onClick={onSave}
        disabled={busy}
        className={`h-9 px-5 ${t.bar} text-white rounded-sm text-sm font-semibold disabled:opacity-50 flex items-center gap-1.5`}
        data-testid={testId}
      >
        <Save size={14} /> {busy ? "Saving…" : "Save"}
      </button>
    </div>
  );
}
